import _ from "lodash";
import {
  CSSProperties,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { Segmented } from "antd";
import {
  StyledAppTabsContainer,
  StyledAppTabsList,
  StyledAppTabsWrapper,
  StyledScrollGradient,
} from "./index.styled";
import { AppLoader } from "..";

export type AppTabProps<T = any> = {
  key: string;
  Title: string | React.ReactNode;
  Icon?: React.ReactNode;
  Component: React.FC<T>;
  isActive?: boolean;
  onClick?: () => void;
  props?: Record<string, any>;
};

export type AppTabsProps<T = any> = {
  tabs: AppTabProps<T>[];
  loading?: boolean;
  selectedTab?: string;
  onChange?: (tab: string) => void;
  sticky?: boolean;
  style?: CSSProperties;
};

// Memoized scroll gradient component
const ScrollGradient = memo<{ position: "left" | "right" }>(({ position }) => (
  <StyledScrollGradient position={position} />
));
ScrollGradient.displayName = "ScrollGradient";

const AppTabs: React.FC<AppTabsProps> = ({
  tabs,
  loading = false,
  selectedTab: initialSelectedTab,
  onChange,
  sticky = false,
  style,
}) => {
  const [selectedTab, setSelectedTab] = useState<string>(
    initialSelectedTab || tabs?.[0]?.key || "",
  );
  const ActiveTab = tabs.find((tab) => tab?.key === selectedTab) || tabs[0];

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false,
  });

  const checkScrollState = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      const newCanScrollLeft = scrollLeft > 0 && scrollWidth > clientWidth;
      const newCanScrollRight = scrollLeft < scrollWidth - clientWidth;

      setScrollState((prevState) => {
        if (
          prevState.canScrollLeft !== newCanScrollLeft ||
          prevState.canScrollRight !== newCanScrollRight
        ) {
          return {
            canScrollLeft: newCanScrollLeft,
            canScrollRight: newCanScrollRight,
          };
        }
        return prevState;
      });
    }
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      checkScrollState();

      // Throttle scroll event to improve performance
      const throttledCheckScrollState = _.throttle(checkScrollState, 16); // ~60fps

      container.addEventListener("scroll", throttledCheckScrollState);
      window.addEventListener("resize", throttledCheckScrollState);

      return () => {
        container.removeEventListener("scroll", throttledCheckScrollState);
        window.removeEventListener("resize", throttledCheckScrollState);
        throttledCheckScrollState.cancel();
      };
    }
  }, [checkScrollState]);

  const handleChange = (value: string) => {
    if (onChange && typeof onChange === "function") {
      onChange(value);
    }
    setSelectedTab(value);
  };

  return (
    <StyledAppTabsContainer $sticky={sticky} style={style}>
      <StyledAppTabsWrapper $sticky={sticky}>
        <StyledAppTabsList ref={scrollContainerRef}>
          <Segmented
            options={tabs.map((tab, index) => ({
              value: tab?.key || index,
              label: (
                <StyledFlex style={{ gap: "5px" }} key={tab?.key || index}>
                  {tab?.Icon} {tab?.Title}
                </StyledFlex>
              ),
            }))}
            value={selectedTab}
            onChange={handleChange}
          />
        </StyledAppTabsList>

        {scrollState.canScrollLeft && <ScrollGradient position="left" />}
        {scrollState.canScrollRight && <ScrollGradient position="right" />}
      </StyledAppTabsWrapper>

      {ActiveTab?.Component ? (
        <>
          {loading ? (
            <AppLoader />
          ) : (
            <ActiveTab.Component
              key={ActiveTab?.key}
              {...(ActiveTab?.props || {})}
            />
          )}
        </>
      ) : (
        <div>Component not found</div>
      )}
    </StyledAppTabsContainer>
  );
};

export default memo(AppTabs);
AppTabs.displayName = "AppTabs";
